import { supabase } from '@/supabase.config';
import type { BankAccount, BankAccountFormData, ReceiptSequence } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function getBankAccounts(): Promise<BankAccount[]> {
  const { data, error } = await db
    .from('bank_accounts')
    .select('*')
    .order('is_active', { ascending: false })
    .order('bank_name');
  if (error) throw error;
  return (data ?? []) as BankAccount[];
}

export async function getActiveBankAccounts(): Promise<BankAccount[]> {
  const { data, error } = await db
    .from('bank_accounts')
    .select('*')
    .eq('is_active', true)
    .order('bank_name');
  if (error) throw error;
  return (data ?? []) as BankAccount[];
}

export async function createBankAccount(
  form: Omit<BankAccountFormData, 'qr_code_file'> & { qr_code_url?: string | null }
): Promise<BankAccount> {
  const { data, error } = await db
    .from('bank_accounts')
    .insert({
      bank_name:      form.bank_name,
      account_name:   form.account_name,
      account_number: form.account_number,
      account_type:   form.account_type || null,
      currency:       form.currency,
      is_active:      form.is_active,
      qr_code_url:    form.qr_code_url ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as BankAccount;
}

export async function updateBankAccount(
  id: string,
  updates: Partial<Omit<BankAccountFormData, 'qr_code_file'> & { qr_code_url?: string | null }>
): Promise<void> {
  const { error } = await db
    .from('bank_accounts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteBankAccount(id: string): Promise<void> {
  const { error } = await db.from('bank_accounts').delete().eq('id', id);
  if (error) throw error;
}

export async function uploadQrCode(accountId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png';
  const path = `qr-codes/${accountId}.${ext}`;
  const { error } = await supabase.storage
    .from('BankAccountFiles')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('BankAccountFiles').getPublicUrl(path);
  return data.publicUrl;
}

export async function getReceiptSequences(): Promise<ReceiptSequence[]> {
  const { data, error } = await db
    .from('receipt_sequences')
    .select('*')
    .order('year', { ascending: false });
  if (error) throw error;
  return (data ?? []) as ReceiptSequence[];
}

export async function resetReceiptSequence(year: number, lastNumber: number): Promise<void> {
  const { error } = await db
    .from('receipt_sequences')
    .upsert({ year, last_number: lastNumber, updated_at: new Date().toISOString() });
  if (error) throw error;
}
